<template>
  <BittsPopover
    trigger="click"
    placement="bottomRight"
    :show-menu="isOverflowMenuOpen"
    class="population-overflow-menu"
    @menu-visible="toggleOverflowMenu"
  >
    <span
      :class="isOverflowMenuOpen ? 'population-overflow-menu__icon-open' : null"
      class="population-overflow-menu__icon-wrapper"
    >
      <FontAwesomeIcon
        :icon="['fak', 'menu']"
        :style="{ height: '18px', width: '18px' }"
        class="population-overflow-menu__icon"
      />
    </span>
    <template #content>
      <div
        class="population-overflow-menu__content flex flex-col text-brand-navy text-sm"
      >
        <button
          v-for="option in options"
          :key="option.label"
          class="population-overflow-menu__item"
          type="button"
          @click="option.onClick"
        >
          <FontAwesomeIcon
            :icon="option.icon"
            :style="{ height: '16px', width: '16px', color: 'currentColor' }"
            class="text-neutral-accent mr-8"
          />
          {{ option.label }}
        </button>
      </div>
    </template>
  </BittsPopover>
</template>

<script setup lang="ts">
import { BittsPopover } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useExport from '@/composables/useExport';
import { useRootStore } from '@/stores/RootStore';
import { Population } from '@/types/populations';

type Props = {
  atOrAboveCap?: boolean;
  canManagePopulations?: boolean;
  isStandard?: boolean;
  population: Population;
  recordNumber?: number | null;
  sharingLink: RouteLocationRaw;
  standardPopulations?: Population[];
};

type EmitEvent =
  | 'duplicate-population'
  | 'convert-to-standard'
  | 'convert-to-custom'
  | 'delete-population';

const props = withDefaults(defineProps<Props>(), {
  atOrAboveCap: false,
  canManagePopulations: true,
  isStandard: false,
  recordNumber: null,
  standardPopulations: () => [],
});

const emit = defineEmits<(e: EmitEvent) => void>();

const { hasScope } = useAuth();
const hasManageSharing = computed(() => hasScope('write:sharing'));
const { hasIntegrationUserRole } = storeToRefs(useRootStore());
const router = useRouter();
const { performExport } = useExport();
const isOverflowMenuOpen = ref(false);
const exportParams = computed(() => {
  return {
    our_population_ids: [props.population?.id],
    overlap_only: false,
    export_name: props.population?.name,
  };
});

function toggleOverflowMenu() {
  isOverflowMenuOpen.value = !isOverflowMenuOpen.value;
}

function toggleAndEmitEvent(event: EmitEvent) {
  toggleOverflowMenu();
  emit(event);
}

const options = computed(() =>
  [
    {
      icon: ['fak', hasManageSharing.value ? 'settings' : 'visible'],
      label: hasManageSharing.value
        ? 'Partner-specific Sharing'
        : 'Default Sharing Settings',
      visible: !hasIntegrationUserRole.value,
      onClick: () => {
        toggleOverflowMenu();
        router.push(props.sharingLink);
      },
    },
    {
      icon: ['fak', 'copy'],
      label: 'Duplicate Population',
      visible: props.canManagePopulations && !props.atOrAboveCap,
      onClick: () => toggleAndEmitEvent('duplicate-population'),
    },
    {
      icon: ['fak', 'cycle'],
      label: 'Convert to Standard',
      visible:
        props.canManagePopulations &&
        !props.atOrAboveCap &&
        props.standardPopulations.length < 3 &&
        !props.isStandard,
      onClick: () => toggleAndEmitEvent('convert-to-standard'),
    },
    {
      icon: ['fak', 'cycle'],
      label: 'Convert to Custom',
      visible:
        props.canManagePopulations && props.isStandard && !props.atOrAboveCap,
      onClick: () => toggleAndEmitEvent('convert-to-custom'),
    },
    {
      icon: ['fak', 'download'],
      label: 'Export',
      visible: props.canManagePopulations && !!props.recordNumber,
      onClick: () =>
        performExport(
          exportParams.value,
          '/v0.3/matches/export',
          props.recordNumber,
        ),
    },
    {
      icon: ['fak', 'delete'],
      label: 'Delete Population',
      visible: props.canManagePopulations,
      onClick: () => toggleAndEmitEvent('delete-population'),
    },
  ].filter((option) => option.visible),
);
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.population-overflow-menu__icon {
  @apply h-14 w-14 cursor-pointer text-neutral-600;
}

.population-overflow-menu__icon-open {
  @apply bg-neutral-100 rounded-bts-sm;
}

.population-overflow-menu__icon-wrapper {
  @apply ml-8 flex items-center justify-center;
  height: 24px;
  width: 24px;
}

.population-overflow-menu__item {
  @apply cursor-pointer px-16 py-4 text-left;
}

.population-overflow-menu__item:hover {
  @apply bg-primary-background-weak;
}
</style>
